import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface PaymentRequestData {
  url: string;
  webservice: string;
  fromAccountId: string;
  toAccountNumber: string;
  amount: number;
  description: string;
  timestamp: string;
  qrCodeDataURL?: string;
}

export interface ChildWalletData {
  childUserId: number;
  name?: string;
  firstName: string;
  lastName: string;
  accountNumber?: string;
  primaryAccountNumber: string;
  primaryAccountBalance: number;
  balance: number;
  savingsGoal?: number;
  transactions?: any[];
  financialGoals?: any[];
  spendingCategories?: any[];
  allowanceDetails?: any;
  avatar?: string;
  age?: number;
  parentName?: string;
  allowance?: number;
  allowanceDay?: string;
  lastAllowance?: string;
}

interface ChildWalletState {
  childWallets: ChildWalletData[];
  selectedChild: ChildWalletData | null;
  activePaymentRequest: PaymentRequestData | null;
  paymentRequestHistory: PaymentRequestData[];
  isLoading: boolean;
  error: string | null;
}

const initialState: ChildWalletState = {
  childWallets: [],
  selectedChild: null,
  activePaymentRequest: null,
  paymentRequestHistory: [],
  isLoading: false,
  error: null,
};

const childWalletSlice = createSlice({
  name: 'childWallet',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setChildWallets: (state, action: PayloadAction<ChildWalletData[]>) => {
      state.childWallets = action.payload;
    },
    setSelectedChild: (state, action: PayloadAction<ChildWalletData | null>) => {
      state.selectedChild = action.payload;
    },
    updateChildWallet: (state, action: PayloadAction<ChildWalletData>) => {
      const index = state.childWallets.findIndex(child => child.childUserId === action.payload.childUserId);
      if (index !== -1) {
        state.childWallets[index] = action.payload;
      }
      if (state.selectedChild?.childUserId === action.payload.childUserId) {
        state.selectedChild = action.payload;
      }
    },
    addChildWallet: (state, action: PayloadAction<ChildWalletData>) => {
      state.childWallets.push(action.payload);
    },
    createPaymentRequest: (state, action: PayloadAction<PaymentRequestData>) => {
      state.activePaymentRequest = action.payload;
      state.paymentRequestHistory.unshift(action.payload); // Add to beginning of history
    },
    clearActivePaymentRequest: (state) => {
      state.activePaymentRequest = null;
    },
    updatePaymentRequestQR: (state, action: PayloadAction<{ requestId: string; qrCodeDataURL: string }>) => {
      if (state.activePaymentRequest && state.activePaymentRequest.timestamp === action.payload.requestId) {
        state.activePaymentRequest.qrCodeDataURL = action.payload.qrCodeDataURL;
      }
      // Also update in history
      const historyIndex = state.paymentRequestHistory.findIndex(req => req.timestamp === action.payload.requestId);
      if (historyIndex !== -1) {
        state.paymentRequestHistory[historyIndex].qrCodeDataURL = action.payload.qrCodeDataURL;
      }
    },
    clearPaymentRequestHistory: (state) => {
      state.paymentRequestHistory = [];
    },
    removePaymentRequestFromHistory: (state, action: PayloadAction<string>) => {
      state.paymentRequestHistory = state.paymentRequestHistory.filter(req => req.timestamp !== action.payload);
    },
    deletePaymentRequest: (state, action: PayloadAction<number>) => {
      state.paymentRequestHistory.splice(action.payload, 1);
    },
    clearChildWalletState: (state) => {
      state.childWallets = [];
      state.selectedChild = null;
      state.activePaymentRequest = null;
      state.paymentRequestHistory = [];
      state.error = null;
    },
  },
});

export const {
  setLoading,
  setError,
  setChildWallets,
  setSelectedChild,
  updateChildWallet,
  addChildWallet,
  createPaymentRequest,
  clearActivePaymentRequest,
  updatePaymentRequestQR,
  clearPaymentRequestHistory,
  removePaymentRequestFromHistory,
  deletePaymentRequest,
  clearChildWalletState,
} = childWalletSlice.actions;

export default childWalletSlice.reducer; 